import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Clock, Plus, Calendar, TrendingUp } from 'lucide-react';

interface UsageEntry {
  id: number;
  clientName: string;
  date: string;
  checkIn: string;
  checkOut: string;
  hoursUsed: number;
  packageType: string;
  cost: number;
}

interface DailyStats {
  date: string;
  totalHours: number;
  totalClients: number;
  revenue: number;
}

export function UsageTracking() {
  const [usageEntries, setUsageEntries] = useState<UsageEntry[]>([
    {
      id: 1,
      clientName: 'John Doe',
      date: '2024-06-18',
      checkIn: '09:00',
      checkOut: '17:00',
      hoursUsed: 8,
      packageType: 'Monthly Premium',
      cost: 0,
    },
    {
      id: 2,
      clientName: 'Sarah Wilson',
      date: '2024-06-18',
      checkIn: '10:30',
      checkOut: '15:30',
      hoursUsed: 5,
      packageType: '30-hour package',
      cost: 0,
    },
    {
      id: 3,
      clientName: 'Mike Johnson',
      date: '2024-06-17',
      checkIn: '08:00',
      checkOut: '12:00',
      hoursUsed: 4,
      packageType: 'Hourly Rate',
      cost: 3300,
    },
    {
      id: 4,
      clientName: 'Emma Brown',
      date: '2024-06-17',
      checkIn: '14:00',
      checkOut: '18:00',
      hoursUsed: 4,
      packageType: '10-hour package',
      cost: 0,
    },
    {
      id: 5,
      clientName: 'Alex Turner',
      date: '2024-06-16',
      checkIn: '11:00',
      checkOut: '16:00',
      hoursUsed: 5,
      packageType: 'Daily Pass',
      cost: 1925,
    },
  ]);

  const [newEntry, setNewEntry] = useState({
    clientName: '',
    checkIn: '',
    checkOut: '',
    packageType: '',
  });

  const clients = ['John Doe', 'Sarah Wilson', 'Mike Johnson', 'Emma Brown', 'Alex Turner', 'Lisa Chen'];
  const packageTypes = ['Monthly Premium', 'Monthly Regular', '30-hour package', '10-hour package', 'Daily Pass', 'Hourly Rate'];

  const calculateHours = (checkIn: string, checkOut: string): number => {
    if (!checkIn || !checkOut) return 0;
    const inTime = new Date(`2024-01-01T${checkIn}`);
    const outTime = new Date(`2024-01-01T${checkOut}`);
    return Math.max(0, (outTime.getTime() - inTime.getTime()) / (1000 * 60 * 60));
  };

  const calculateCost = (hours: number, packageType: string): number => {
    switch (packageType) {
      case 'Hourly Rate':
        return hours * 825;
      case 'Daily Pass':
        return 1925;
      case 'Monthly Premium':
      case 'Monthly Regular':
      case '30-hour package':
      case '10-hour package':
        return 0; // Already paid
      default:
        return 0;
    }
  };

  const handleAddEntry = () => {
    if (!newEntry.clientName || !newEntry.checkIn || !newEntry.checkOut || !newEntry.packageType) return;

    const hoursUsed = calculateHours(newEntry.checkIn, newEntry.checkOut);
    const cost = calculateCost(hoursUsed, newEntry.packageType);

    const entry: UsageEntry = {
      id: Math.max(...usageEntries.map(e => e.id)) + 1,
      ...newEntry,
      date: new Date().toISOString().split('T')[0],
      hoursUsed,
      cost,
    };

    setUsageEntries([...usageEntries, entry]);
    setNewEntry({ clientName: '', checkIn: '', checkOut: '', packageType: '' });
  };

  // Calculate daily stats
  const dailyStats: DailyStats[] = usageEntries.reduce((acc: DailyStats[], entry) => {
    const existingDay = acc.find(day => day.date === entry.date);
    if (existingDay) {
      existingDay.totalHours += entry.hoursUsed;
      existingDay.totalClients += 1;
      existingDay.revenue += entry.cost;
    } else {
      acc.push({
        date: entry.date,
        totalHours: entry.hoursUsed,
        totalClients: 1,
        revenue: entry.cost,
      });
    }
    return acc;
  }, []).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const totalHoursToday = usageEntries
    .filter(entry => entry.date === new Date().toISOString().split('T')[0])
    .reduce((sum, entry) => sum + entry.hoursUsed, 0);

  const totalClientsToday = new Set(
    usageEntries
      .filter(entry => entry.date === new Date().toISOString().split('T')[0])
      .map(entry => entry.clientName)
  ).size;

  const totalRevenueToday = usageEntries
    .filter(entry => entry.date === new Date().toISOString().split('T')[0])
    .reduce((sum, entry) => sum + entry.cost, 0);

  const getPackageTypeColor = (type: string) => {
    switch (type) {
      case 'Monthly Premium':
        return 'bg-purple-100 text-purple-800';
      case 'Monthly Regular':
        return 'bg-blue-100 text-blue-800';
      case 'Hourly Rate':
        return 'bg-green-100 text-green-800';
      case 'Daily Pass':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Today's Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Hours Today</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{totalHoursToday}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Clients Today</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{totalClientsToday}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Revenue Today</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">₱{totalRevenueToday.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Log Hours */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Usage Tracking</CardTitle>
              <CardDescription>Log and monitor client workspace usage</CardDescription>
            </div>
            <Dialog>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Log Hours
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Log Usage</DialogTitle>
                <DialogDescription>Record client workspace usage</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="client">Client</Label>
                  <Select value={newEntry.clientName} onValueChange={(value) => setNewEntry({ ...newEntry, clientName: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client} value={client}>{client}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="checkin">Check In</Label>
                    <Input
                      id="checkin"
                      type="time"
                      value={newEntry.checkIn}
                      onChange={(e) => setNewEntry({ ...newEntry, checkIn: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="checkout">Check Out</Label>
                    <Input
                      id="checkout"
                      type="time"
                      value={newEntry.checkOut}
                      onChange={(e) => setNewEntry({ ...newEntry, checkOut: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="package">Package Type</Label>
                  <Select value={newEntry.packageType} onValueChange={(value) => setNewEntry({ ...newEntry, packageType: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select package type" />
                    </SelectTrigger>
                    <SelectContent>
                      {packageTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {newEntry.checkIn && newEntry.checkOut && (
                  <div className="p-3 bg-muted rounded-md">
                    <p className="text-sm">
                      Hours: <span className="font-medium">{calculateHours(newEntry.checkIn, newEntry.checkOut).toFixed(1)}</span>
                    </p>
                    <p className="text-sm">
                      Cost: <span className="font-medium">₱{calculateCost(calculateHours(newEntry.checkIn, newEntry.checkOut), newEntry.packageType).toLocaleString()}</span>
                    </p>
                  </div>
                )}
                <Button onClick={handleAddEntry} className="w-full">
                  Log Usage
                </Button>
              </div>
            </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* Usage History */}
      <Card>
        <CardHeader>
          <CardTitle>Usage History</CardTitle>
          <CardDescription>Recent workspace usage entries</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Check In</TableHead>
                <TableHead>Check Out</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usageEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.clientName}</TableCell>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.checkIn}</TableCell>
                  <TableCell>{entry.checkOut}</TableCell>
                  <TableCell>{entry.hoursUsed.toFixed(1)}</TableCell>
                  <TableCell>
                    <Badge className={getPackageTypeColor(entry.packageType)}>
                      {entry.packageType}
                    </Badge>
                  </TableCell>
                  <TableCell>₱{entry.cost.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Daily Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Summary</CardTitle>
          <CardDescription>Daily usage statistics</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Total Hours</TableHead>
                <TableHead>Unique Clients</TableHead>
                <TableHead>Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dailyStats.map((day, index) => (
                <TableRow key={index}>
                  <TableCell>{day.date}</TableCell>
                  <TableCell>{day.totalHours.toFixed(1)}</TableCell>
                  <TableCell>{day.totalClients}</TableCell>
                  <TableCell>₱{day.revenue.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}